import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import i18n from "./language/i18n";

import MainLayout from "./layouts/MainLayout";
import Welcome from "./pages/Welcome";
import InstallGopaperlessAgent from "./pages/InstallGopaperlessAgent";
import Version from "./pages/Version";
import UnInstallGopaperlessAgent from "./pages/UnInstallGopaperlessAgent";
import LoginGopaperlessAgent from "./pages/LoginGopaperlessAgent";
import ViewCert from "./pages/ViewCert";
import MessBox from "./pages/MessBox";
import SupportSign from "./pages/SupportSign";
import Word from "./pages/Word";
import Excel from "./pages/Excel";
import PowerPoint from "./pages/PowerPoint";
import Outlook from "./pages/Outlook";
import Foxit from "./pages/Foxit";
import PDFSign from "./pages/PDFSign";
import AcrobatReader from "./pages/AcrobatReader";
import AcrobatWriter from "./pages/AcrobatWriter";
import Pricing from "./pages/Pricing";
import InstallationGuide from "./pages/InstallationGuide";
import UserGuide from "./pages/UserGuide";
import UseCase from "./pages/UseCase";
import Cookies from "./components/cookies/cookies";
import CookiePreferencesModal from "./components/cookies/CookiePreferencesModal";
import CookieFloatingButton from "./components/cookies/CookieFloatingButton";
import AnalyticsTracker from "./components/ga4/AnalyticsTracker";
import SEO from "./components/seo/SEO";
import GuideToAddingandTrustingDigitalCertificatesFromCAsOnMacOS from "./pages/GuideToAddingandTrustingDigitalCertificatesFromCAsOnMacOS";
import ConfigGopaperlessAgent from "./pages/ConfigGopaperlessAgent";
import {
  applyCookiePreferences,
  clearAccessLangCookie,
  getCookiePreferences,
  getPathLang,
  isFunctionalCookieAllowed,
  resolveAccessLang,
  setAccessLangCookie,
  subscribeCookieConsentChange,
} from "./utils/langCookie";

const DEFAULT_PREFS = {
  functional: false,
  analytics: false,
  marketing: false,
};

/** Redirect `/` theo cookie ngôn ngữ — chỉ khi functional === true; mặc định EN */
function RootRedirect() {
  const location = useLocation();
  const lang = resolveAccessLang("en");
  const hash = location.hash || "";
  const search = location.search || "";
  return <Navigate to={`/${lang}${search}${hash}`} replace />;
}

/**
 * Đồng bộ i18n + cookie ngôn ngữ theo :lang trên URL.
 * Mỗi lần đổi lang đều đọc lại consent (cross-tab: Tab khác tắt toggle → không ghi cookie).
 */
function LanguageSync() {
  const { lang } = useParams();

  useEffect(() => {
    if (lang !== "en" && lang !== "vi") return;

    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }

    // Chỉ ghi cookie khi functional === true (đọc live từ document.cookie)
    setAccessLangCookie(lang);
  }, [lang]);

  // Khi tab khác tắt/bật toggle:
  // - functional tắt → xóa cookie dư (an toàn cross-tab)
  // - functional bật → đồng bộ cookie theo URL hiện tại
  useEffect(() => {
    return subscribeCookieConsentChange(() => {
      if (!isFunctionalCookieAllowed()) {
        clearAccessLangCookie();
        return;
      }
      setAccessLangCookie(getPathLang());
    });
  }, []);

  return null;
}

function AppRoutes() {
  const { lang } = useParams();
  const location = useLocation();

  if (lang !== "en" && lang !== "vi") {
    return <Navigate to={`/${resolveAccessLang("en")}`} replace />;
  }

  // Chuẩn hóa bỏ trailing slash: /vi/ → /vi, /en/installation/ → /en/installation
  // Tránh lệch path document/source khi URL kết thúc bằng /
  if (location.pathname.length > 1 && location.pathname.endsWith("/")) {
    const clean = location.pathname.replace(/\/+$/, "") || `/${lang}`;
    return <Navigate to={`${clean}${location.search}${location.hash}`} replace />;
  }

  return (
    <>
      <LanguageSync />
      <AnalyticsTracker />
      <SEO />
      <MainLayout>
        <Routes>
          <Route index element={<Welcome />} />
          <Route path="version" element={<Version />} />
          <Route path="installation" element={<InstallationGuide />} />
          <Route path="install-gopaperlessagent" element={<InstallGopaperlessAgent />} />
          <Route path="uninstall-gopaperlessagent" element={<UnInstallGopaperlessAgent />} />
          <Route
            path="guide-to-adding-and-trusting-digital-certificates-from-cas-on-macos"
            element={<GuideToAddingandTrustingDigitalCertificatesFromCAsOnMacOS />}
          />
          <Route path="userguide" element={<UserGuide />} />
          <Route path="config-gopaperlessagent" element={<ConfigGopaperlessAgent />} />
          <Route path="login-gopaperlessagent" element={<LoginGopaperlessAgent />} />
          <Route path="view-cert" element={<ViewCert />} />
          <Route path="message-box" element={<MessBox />} />
          <Route path="supported-sign" element={<SupportSign />} />
          <Route path="usecase" element={<UseCase />} />
          <Route path="cases/word" element={<Word />} />
          <Route path="cases/excel" element={<Excel />} />
          <Route path="cases/powerpoint" element={<PowerPoint />} />
          <Route path="cases/outlook" element={<Outlook />} />
          <Route path="cases/foxit" element={<Foxit />} />
          <Route path="cases/pdf-sign" element={<PDFSign />} />
          <Route path="cases/acrobat-reader" element={<AcrobatReader />} />
          <Route path="cases/acrobat-writer" element={<AcrobatWriter />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="*" element={<Navigate to={`/${lang}`} replace />} />
        </Routes>
      </MainLayout>
    </>
  );
}

function App() {
  const [, setNonce] = useState("");
  const [showCookies, setShowCookies] = useState(false);
  const [openPreferences, setOpenPreferences] = useState(false);
  const [showCookieButton, setShowCookieButton] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFS);

  /** Đồng bộ UI preferences từ cookie (cùng tab + cross-tab) */
  const syncPreferencesFromCookie = () => {
    const prefs = getCookiePreferences();
    if (!prefs) {
      setShowCookies(true);
      setShowCookieButton(false);
      setPreferences(DEFAULT_PREFS);
      return;
    }
    setPreferences({
      functional: prefs.functional === true,
      analytics: prefs.analytics === true,
      marketing: prefs.marketing === true,
    });
    setShowCookies(false);
    setShowCookieButton(true);
  };

  useEffect(() => {
    syncPreferencesFromCookie();
    return subscribeCookieConsentChange(syncPreferencesFromCookie);
  }, []);

  useEffect(() => {
    fetch("/auth/nonce")
      .then((res) => {
        const nonce = res.headers.get("x-nonce");
        if (!nonce) return;
        setNonce(nonce);
        window.nonce = nonce;
        document.getElementById("nonce-meta")?.setAttribute("data-nonce", nonce);
        document.querySelectorAll("script, style").forEach((tag) => {
          if (!tag.hasAttribute("nonce")) tag.setAttribute("nonce", nonce);
        });
      })
      .catch(() => {
        // Offline / mạng lỗi — không chặn UI cookie
      });
  }, []);

  /**
   * Chỉ commit khi Accept / Reject / Lưu — tránh race khi toggle nhanh trong popup.
   * currentLang lấy từ URL tại thời điểm Save (bật toggle không đổi lang → vẫn tạo cookie đúng).
   */
  const commitPreferences = (prefs) => {
    const next = {
      functional: prefs?.functional === true,
      analytics: prefs?.analytics === true,
      marketing: prefs?.marketing === true,
    };
    setPreferences(next);
    applyCookiePreferences(next, getPathLang());
    setShowCookies(false);
    setShowCookieButton(true);
  };

  const handleAcceptAll = () => {
    commitPreferences({
      functional: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    commitPreferences({
      functional: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = (draftPrefs) => {
    // Chỉ nhận draft lúc bấm Lưu — trạng thái cuối cùng, không xen kẽ Bật/Tắt giữa chừng
    commitPreferences(draftPrefs || preferences);
    setOpenPreferences(false);
  };

  /** Cancel / đóng popup: không áp dụng draft — khôi phục từ cookie đã lưu */
  const handleClosePreferences = () => {
    syncPreferencesFromCookie();
    setOpenPreferences(false);
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/:lang/*" element={<AppRoutes />} />
      </Routes>

      {showCookies && (
        <Cookies
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onPreferences={() => setOpenPreferences(true)}
        />
      )}

      <CookiePreferencesModal
        open={openPreferences}
        onClose={handleClosePreferences}
        preferences={preferences}
        onSave={handleSavePreferences}
      />

      {showCookieButton && (
        <CookieFloatingButton onClick={() => setOpenPreferences(true)} />
      )}
    </BrowserRouter>
  );
}

export default App;
